import { useState, useEffect } from 'react';

interface CountdownTimerProps {
  targetMs?: number;
  startTime?: string | Date | number;
  endTime?: string | Date | number;
  className?: string;
  showLabel?: boolean;
  compact?: boolean;
  // Backward compatibility props
  onComplete?: () => void;
  withLargeNums?: boolean;
}

export function CountdownTimer({
  targetMs,
  startTime,
  endTime,
  className = '',
  showLabel = true,
  compact = false,
  onComplete,
  withLargeNums = false,
}: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState(() => {
    if (targetMs !== undefined) {
      return targetMs;
    }

    if (startTime && endTime) {
      const start =
        typeof startTime === 'number'
          ? startTime
          : new Date(startTime).getTime();
      const end =
        typeof endTime === 'number' ? endTime : new Date(endTime).getTime();
      const now = Date.now();

      if (now >= start && now <= end) {
        return end - now;
      }
    }

    return 0;
  });

  useEffect(() => {
    if (timeLeft <= 0) {
      if (onComplete) onComplete();
      return;
    }

    const interval = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft, onComplete]);

  const minutes = Math.floor(timeLeft / 60000);
  const seconds = Math.floor((timeLeft % 60000) / 1000);

  if (timeLeft <= 0) {
    return null;
  }

  // Backward compatibility: withLargeNums mode
  if (withLargeNums) {
    return (
      <span
        className={`inline-flex items-center gap-2 font-mono text-5xl ${className}`}
      >
        <span className='w-[2ch] text-5xl'>
          {minutes.toString().padStart(2, '0')}
        </span>
        <span>:</span>
        <span className='w-[2ch] text-5xl'>
          {seconds.toString().padStart(2, '0')}
        </span>
      </span>
    );
  }

  // Compact mode
  if (compact) {
    return (
      <div
        className={`flex items-center gap-1 text-yellow-400 text-xs font-mono tabular-nums ${className}`}
      >
        <span>
          {minutes.toString().padStart(2, '0')}:
          {seconds.toString().padStart(2, '0')}
        </span>
      </div>
    );
  }

  // Default mode
  return (
    <div
      className={`flex items-center gap-1 text-gray-400 text-sm font-mono tabular-nums ${className}`}
    >
      {showLabel && <span>Should finish in:</span>}
      <span className='min-w-[1ch]'>{minutes.toString().padStart(2, '0')}</span>
      <span>m</span>
      <span className='min-w-[1ch]'>{seconds.toString().padStart(2, '0')}</span>
      <span>s</span>
    </div>
  );
}
